import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Clock, 
  DollarSign,
  User,
  Mail,
  Calendar,
  Image,
  Zap,
  RefreshCw
} from 'lucide-react';

const AdminDonationsPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [pending, setPending] = useState([]);
  const [allDonations, setAllDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/admin-login');
      return;
    }
    
    if (user.role !== 'admin') {
      toast.error('Acesso negado');
      navigate('/dashboard');
      return;
    }
    
    fetchDonations();
  }, [user, authLoading, navigate]);

  const fetchDonations = async () => {
    setLoading(true);
    try {
      const [pendingRes, allRes] = await Promise.all([
        api.get('/api/donations/pending'),
        api.get('/api/donations/all?limit=20')
      ]);
      setPending(pendingRes.data.pending || []);
      setAllDonations(allRes.data.donations || []);
    } catch (error) {
      toast.error('Erro ao carregar doações');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (donationId) => {
    setProcessing(donationId);
    try {
      const response = await api.post(`/api/donations/approve/${donationId}`);
      toast.success(response.data.message);
      fetchDonations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (donationId) => {
    if (!window.confirm('Tem certeza que deseja rejeitar esta doação?')) return;
    
    setProcessing(donationId);
    try {
      await api.post(`/api/donations/reject/${donationId}`);
      toast.success('Doação rejeitada');
      fetchDonations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao rejeitar');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" /> Pendente</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" /> Aprovado</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" /> Rejeitado</Badge>;
      default:
        return null;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002FA7]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F8]">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-2">
                  <DollarSign className="h-6 w-6 text-[#002FA7]" />
                  Doações para Créditos IA
                </h1>
                <p className="text-sm text-gray-500">Aprovar comprovantes de PIX</p>
              </div>
            </div>
            <Button variant="outline" onClick={fetchDonations}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Pending Section */}
        <div className="mb-8">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            Pendentes ({pending.length})
          </h2>
          
          {pending.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-300" />
              <p>Nenhuma doação pendente</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {pending.map((donation) => (
                <div 
                  key={donation.donation_id}
                  className="bg-white border border-amber-200 rounded-lg p-4 shadow-sm"
                >
                  <div className="flex flex-col lg:flex-row gap-4">
                    {/* Proof Image */}
                    <div 
                      className="w-full lg:w-48 h-48 bg-gray-100 rounded-lg overflow-hidden cursor-pointer flex-shrink-0"
                      onClick={() => setSelectedImage(donation.proof_image)}
                    >
                      {donation.proof_image ? (
                        <img 
                          src={donation.proof_image} 
                          alt="Comprovante" 
                          className="w-full h-full object-cover hover:scale-105 transition-transform"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Image className="h-8 w-8" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-lg flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            {donation.user_name || 'Sem nome'}
                          </p>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {donation.user_email}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-600">R$ {donation.amount?.toFixed(2)}</p>
                          <p className="text-sm text-purple-600 flex items-center gap-1 justify-end">
                            <Zap className="h-3 w-3" />
                            {donation.credits_to_grant} créditos
                          </p>
                        </div>
                      </div>

                      <p className="text-xs text-gray-400 mb-4 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Enviado em: {formatDate(donation.created_at)}
                      </p>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApprove(donation.donation_id)}
                          disabled={processing === donation.donation_id}
                          className="bg-green-600 hover:bg-green-700 flex-1"
                        >
                          {processing === donation.donation_id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Aprovar
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleReject(donation.donation_id)}
                          disabled={processing === donation.donation_id}
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Rejeitar
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* History Section */}
        <div>
          <h2 className="text-lg font-bold mb-4">Histórico Recente</h2>
          
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Usuário</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Valor</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {allDonations.map((donation) => (
                  <tr key={donation.donation_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-sm">{donation.user_name || '-'}</p>
                      <p className="text-xs text-gray-500">{donation.user_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-green-600">R$ {donation.amount?.toFixed(2)}</span>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(donation.status)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(donation.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {allDonations.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Nenhuma doação registrada
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <img 
            src={selectedImage} 
            alt="Comprovante ampliado" 
            className="max-w-full max-h-full rounded-lg"
          />
        </div>
      )}
    </div>
  );
};

export default AdminDonationsPage;
